import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { map, prop } from "ramda"
import { findById, pipeline } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import { getRecordEditId } from "../../../router/selectors"
import type { OptionGroupsTableDataItem } from "./OptionGroupsTable"
import type { OptionGroupFormItem } from "./OptionGroupForm"
import type { AddedToItemsTableItem } from "../components/AddedToItemsTable"
import type { StateObject } from "../../../redux/types"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import type { AddedToItem, CategoryOptionGroup, CategoryOptionGroupResponse } from "../../../../graphql/types"
import { computeOverriddenValue, applyFiltersToTableData, formatOptionsPriceRange } from "../utils"
import { getItemsCategoriesSearchValue, getOptionGroupsData } from "../selectors"
import { ItemsAndCategoriesTableFilters } from "../types"

export const getOptionGroupById = (state: StateObject, id: Maybe<string>) => {
    const optionGroups: Maybe<CategoryOptionGroupResponse> = getOptionGroupsData(state)

    if (!optionGroups || !id) {
        return null
    }

    const group: Maybe<CategoryOptionGroup> = findById(id, optionGroups.data)

    if (!group) {
        return null
    }

    return group
}

export const getEditGroupId = (state: StateObject): Maybe<string> => getRecordEditId(state)

export const getDataForOptionGroupsTable = (
    state: StateObject,
    filters: ItemsAndCategoriesTableFilters
): OptionGroupsTableDataItem[] => {
    const selectedLocationId = getSelectedLocationOverrideId(state)
    const optionGroups: Maybe<CategoryOptionGroupResponse> = getOptionGroupsData(state)
    const searchValue = getItemsCategoriesSearchValue(state)

    if (!optionGroups) {
        return []
    }

    const data = optionGroups.data.map(group => {
        const isActiveOverriddenValue = computeOverriddenValue(
            optionGroups.overrides,
            selectedLocationId,
            group.id,
            prop("isActive")
        )
        const priceOverriddenValue = computeOverriddenValue(
            optionGroups.overrides,
            selectedLocationId,
            group.id,
            prop("price")
        )
        return {
            id: group.id,
            name: group.name,
            price: {
                value:
                    priceOverriddenValue != null
                        ? // @ts-expect-error
                          formatOptionsPriceRange(priceOverriddenValue)
                        : formatOptionsPriceRange(group.price),
                override:
                    priceOverriddenValue != null &&
                    JSON.stringify(priceOverriddenValue) !== JSON.stringify(group.price),
            },
            isActive: isActiveOverriddenValue != null ? isActiveOverriddenValue : group.isActive,
            isActiveOverriden: isActiveOverriddenValue != null && isActiveOverriddenValue !== group.isActive,
            isSelectable: true,
        }
    })

    // @ts-expect-error
    return applyFiltersToTableData(data, searchValue, filters)
}

const buildOptionGroupFormItem = (
    state: StateObject,
    group: CategoryOptionGroup,
    optionGroups: CategoryOptionGroupResponse
): OptionGroupFormItem => {
    const selectedLocationId = getSelectedLocationOverrideId(state)
    const overriddenValue = computeOverriddenValue(
        optionGroups.overrides,
        selectedLocationId,
        group.id,
        prop("isActive")
    )
    return {
        optionGroupId: group.id,
        name: group.name,
        displayName: group.displayName,
        display: group.displayType === "scale" ? "scale" : "list",
        // @ts-expect-error
        isActive: overriddenValue != null ? overriddenValue : group.isActive,
        isActiveOverriden: overriddenValue != null && overriddenValue !== group.isActive,
        items: pipeline(
            group.options,
            map(o => ({
                groupItemId: o.id,
                optionId: o.categoryId,
                name: o.name,
                kitchenName: o.kitchenName,
            }))
        ),
    }
}

export const getOptionGroupFormData = (state: StateObject): Maybe<OptionGroupFormItem> => {
    const optionGroups: Maybe<CategoryOptionGroupResponse> = getOptionGroupsData(state)
    const editGroupId = getEditGroupId(state)

    if (!optionGroups || !editGroupId) {
        return null
    }

    const group: Maybe<CategoryOptionGroup> = findById(editGroupId, optionGroups.data)

    if (!group) {
        return null
    }

    return buildOptionGroupFormItem(state, group, optionGroups)
}

export const getOptionGroupFormItemById = (state: StateObject, id: Maybe<string>): Maybe<OptionGroupFormItem> => {
    const optionGroups: Maybe<CategoryOptionGroupResponse> = getOptionGroupsData(state)

    if (!optionGroups || !id) {
        return null
    }

    const group: Maybe<CategoryOptionGroup> = findById(id, optionGroups.data)

    if (!group) {
        return null
    }

    return buildOptionGroupFormItem(state, group, optionGroups)
}

export const getDataFroAddedToItemsTable = (state: StateObject): AddedToItemsTableItem[] => {
    const optionGroups: Maybe<CategoryOptionGroupResponse> = getOptionGroupsData(state)
    const editGroupId = getEditGroupId(state)

    if (!optionGroups || !editGroupId) {
        return []
    }

    const editOptionGroup = findById(editGroupId, optionGroups.data)

    if (!editOptionGroup) {
        return []
    }

    return editOptionGroup.addedToItems.map((c: AddedToItem) => ({
        id: c.id,
        name: c.name,
        icon: c.icon,
        color: c.color,
    }))
}
